import '@banno/jha-wc/src/progress/jha-progress/jha-progress';
import '@banno/jha-wc/src/tables/advanced/jha-advanced-table/jha-advanced-table';
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { CompanyUserService } from '@treasury/domain/company/user';
import { AccountAccess } from '@treasury/domain/company/user/entities/access';
import '@treasury/presentation/components/jh-ui/tm-button';
import '@treasury/presentation/components/jha-wc/tm-table';
// eslint-disable-next-line no-duplicate-imports
import type { TmTableOptions } from '@treasury/presentation/components/jha-wc/tm-table';
import { TmWizardStep } from '@treasury/presentation/components/tm-wizard';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createAccessToView, createAvailableView } from './company-user-account-access-table.views';

@customElement('tm-company-user-account-access')
export class CompanyUserAccountAccess extends TmWizardStep {
    public stepLabel = 'Account Access';

    @property()
    declare private readonly companyId: number;

    @property()
    declare private readonly userId: number;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @state()
    private accounts: AccountAccess[] = [];

    @state()
    private loading = false;

    @state()
    private saving = false;

    public validate() {
        return true;
    }

    public get hasAtLeastOneAccount() {
        return this.accounts.filter(a => a.hasPendingUserAccess ?? a.hasUserAccess).length > 0;
    }

    public render() {
        if (this.loading) {
            return html`<jha-progress></jha-progress>`;
        }
        return html`
            <tm-table .options=${this.options} .items=${this.accounts}></tm-table>
            <div class="flex justify-end">
                <tm-jh-button
                    appearance="primary"
                    @click=${this.handleContinue}
                    ?disabled=${!this.hasAtLeastOneAccount}
                    label="Continue"
                ></tm-jh-button>
            </div>
        `;
    }

    protected async firstUpdated() {
        this.loading = true;
        await this.hydrateAccountAccess();
        this.loading = false;
    }

    private get options(): TmTableOptions<AccountAccess> {
        return {
            displayAllView: false,
            views: this.views,
        };
    }

    private get views() {
        return [
            createAccessToView((selected: AccountAccess[]) => this.handleAccountRemoval(selected)),
            createAvailableView((selected: AccountAccess[]) =>
                this.handleAccountSelection(selected)
            ),
        ];
    }

    private async hydrateAccountAccess() {
        try {
            const { accounts } = await this.companyUserService.getCompanyUserAccountAccess(
                this.userId,
                this.companyId
            );
            this.accounts = accounts;
        } catch {
            this.toastService.show('Failed to load account access', TmToastPosture.Danger, true);
        }
    }

    private handleContinue() {
        this.dispatchEvent(new CustomEvent('continue'));
    }

    private async handleAccountSelection(selected: AccountAccess[]) {
        try {
            this.saving = true;
            this.dispatchEvent(new CustomEvent('begin-save'));
            const success = await this.companyUserService.addCompanyUserAccessToAccounts(
                this.userId,
                selected,
                this.companyId
            );
            if (!success) {
                throw new Error('Failed to add account access');
            }
            await this.hydrateAccountAccess();
            this.toastService.show('Successfully added account access', TmToastPosture.Success);
        } catch {
            this.toastService.show('Failed to add account access', TmToastPosture.Danger, true);
        } finally {
            this.saving = false;
            this.dispatchEvent(new CustomEvent('save'));
        }
    }

    private async handleAccountRemoval(selected: AccountAccess[]) {
        try {
            this.saving = true;
            this.dispatchEvent(new CustomEvent('begin-save'));
            const success = await this.companyUserService.removeCompanyUserAccessToAccounts(
                this.userId,
                selected,
                this.companyId
            );
            if (!success) {
                throw new Error('Failed to remove accounts');
            }
            await this.hydrateAccountAccess();
            this.toastService.show('Successfully removed account access', TmToastPosture.Success);
        } catch {
            this.toastService.show('Failed to remove account access', TmToastPosture.Danger, true);
        } finally {
            this.saving = false;
            this.dispatchEvent(new CustomEvent('save'));
        }
    }
}
